import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, CalendarDays, Camera, CheckCircle2, ChevronLeft, ChevronRight, FileText, ImagePlus, Loader2,
  PackageCheck, ScanLine, Search, Trash2, X, ZoomIn,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button, Card, ConfirmDeleteDialog, Field, Input, Select } from "../../components/ui-kit";
import {
  compressImage, decodeBarcodeFromBlob, formatIt, parseQuickDate, photoUrl, startLiveScan, vibrate,
} from "../../lib/smartLotto";

const err = (e) => formatApiErrorDetail(e.response?.data?.detail);
const searchable = (value) => (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
const BASE = "/app/smart-lotto";
const UNITS = ["conf", "pz", "kg", "g", "l", "ml"];
const MISSING_LABEL = { prodotto: "prodotto", lotto: "lotto", scadenza: "scadenza" };
const SOURCE_LABEL = { gs1: "letto dal codice della confezione", barcode: "riconosciuto dal barcode", manuale: "scritto a mano" };
const CHECK_QUESTIONS = [
  { key: "transport_temp_ok", label: "Automezzo con vano a temperatura controllata: controllo visivo del dispositivo di temperatura", hint: "“Non applicabile” per la merce a temperatura ambiente", na: true },
  { key: "packaging_ok", label: "Merce ben confezionata, imballi integri" },
  { key: "labeling_ok", label: "Etichettatura e scadenze corrette e leggibili" },
  { key: "documents_ok", label: "Presenti i documenti di accompagnamento specifici (DDT o fattura)" },
];
// Barra azioni sempre a portata di pollice, appoggiata sopra la barra di navigazione
// mobile dell'app (59 px + area sicura, visibile sotto lg): mai coperta da essa.
const ACTION_BAR = "fixed inset-x-0 bottom-[calc(59px+env(safe-area-inset-bottom))] z-30 border-t border-hairline bg-white/95 p-3 shadow-[0_-8px_24px_rgba(10,31,68,.08)] backdrop-blur lg:static lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none";
// Tutto ciò che entra è conforme in modo predefinito: l'operatore cambia solo la voce con un'anomalia.
const ALL_OK = { transport_temp_ok: "si", packaging_ok: "si", labeling_ok: "si", documents_ok: "si" };

// Coda unica delle modifiche: la bozza si salva per intero lato server, quindi
// due richieste contemporanee (foto a raffica) non devono sovrapporsi.
function useSerialQueue() {
  const tail = useRef(Promise.resolve());
  return useCallback((task) => {
    const run = tail.current.then(task, task);
    tail.current = run.catch(() => {});
    return run;
  }, []);
}

function BigAction({ icon: Icon, title, subtitle, onClick, testid, tone = "navy" }) {
  const tones = { navy: "bg-navy text-white", white: "bg-white text-navy border-2 border-navy", red: "smart-blink-red text-white" };
  const dark = tone !== "white";
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`flex w-full items-center gap-4 rounded-2xl p-5 text-left shadow-card transition active:scale-[0.99] sm:p-6 ${tones[tone]}`}>
      <span className={`grid h-14 w-14 shrink-0 place-items-center rounded-xl ${dark ? "bg-white/10" : "bg-navy/5"}`}><Icon className="h-7 w-7" /></span>
      <span>
        <span className="block font-body text-lg font-bold uppercase tracking-wide">{title}</span>
        <span className={`block text-sm ${dark ? "text-white/75" : "text-slate-500"}`}>{subtitle}</span>
      </span>
    </button>
  );
}

function Lightbox({ src, onClose }) {
  const [zoom, setZoom] = useState(false);
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-black/95" role="dialog" aria-modal="true" aria-label="Foto ingrandita">
      <div className="flex items-center justify-between p-2 text-white">
        <span className="px-2 text-sm">{zoom ? "Trascina per spostarti" : "Tocca la foto per ingrandire"}</span>
        <button type="button" onClick={onClose} className="grid h-11 w-11 place-items-center rounded-full hover:bg-white/10" aria-label="Chiudi"><X className="h-6 w-6" /></button>
      </div>
      <div className="flex-1 overflow-auto">
        <img src={src} alt="Etichetta" onClick={() => setZoom(!zoom)}
          className={zoom ? "max-w-none w-[250%]" : "mx-auto h-full w-full object-contain"} />
      </div>
    </div>
  );
}

function LiveScanner({ open, onClose, onText }) {
  const videoRef = useRef(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return undefined;
    let controls = null;
    let done = false;
    setError("");
    startLiveScan(videoRef.current, (text) => {
      if (done) return;
      done = true;
      vibrate(80);
      controls?.stop();
      onText(text);
    }).then((c) => { controls = c; if (done) c.stop(); })
      .catch(() => setError("Fotocamera non disponibile o permesso negato. Puoi comunque scrivere i dati a mano."));
    return () => { done = true; controls?.stop(); };
  }, [open, onText]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-black" role="dialog" aria-modal="true" aria-label="Lettura barcode">
      <div className="flex items-center justify-between p-3 text-white">
        <span className="text-sm font-medium">Inquadra il barcode, il DataMatrix o il QR della confezione</span>
        <button type="button" onClick={onClose} className="grid h-11 w-11 place-items-center rounded-full hover:bg-white/10" aria-label="Chiudi"><X className="h-6 w-6" /></button>
      </div>
      <video ref={videoRef} className="flex-1 object-cover" muted playsInline />
      {error && <p className="bg-red-600 p-4 text-sm text-white">{error}</p>}
    </div>
  );
}

function ItemBadge({ item }) {
  if (item.pending) return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"><Loader2 className="h-3 w-3 animate-spin" /> salvo…</span>;
  if (!item.missing?.length) {
    const warn = item.warnings?.length;
    return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${warn ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}>{warn ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}{warn ? "Da controllare" : "Completo"}</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800"><AlertTriangle className="h-3 w-3" /> Manca {item.missing.map((m) => MISSING_LABEL[m]).join(", ")}</span>;
}

function MissingGuide({ item }) {
  const lot = item.missing?.includes("lotto");
  const exp = item.missing?.includes("scadenza");
  if (!lot && !exp) return null;
  const found = [!lot && "Lotto trovato.", !exp && "Scadenza trovata."].filter(Boolean).join(" ");
  const notFound = [lot && "lotto", exp && "scadenza"].filter(Boolean).join(" e ");
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="smart-missing-guide">
      <p className="font-semibold">{found} {notFound[0].toUpperCase() + notFound.slice(1)} da inserire.</p>
      <p className="mt-1">Cerca la dicitura {lot ? <b>LOTTO / LOT / L</b> : null}{lot && exp ? " e " : ""}{exp ? <b>da consumarsi entro / preferibilmente entro / SCAD</b> : null}. Controlla:</p>
      <ul className="mt-1 list-disc pl-5">
        <li>fondo della confezione</li><li>bordo superiore o tappo</li><li>saldatura della busta</li><li>retro, vicino alla tabella nutrizionale</li><li>vicino all'altra data già trovata</li>
      </ul>
      <p className="mt-2">Se non riesci a leggerlo bene, scatta una <b>foto di dettaglio</b>: resta collegata a questo prodotto.</p>
    </div>
  );
}

export default function SmartLotto() {
  const { ctx } = useOutletContext();
  const sites = ctx.sites || [];
  // Nuovo prodotto anche da addetto/responsabile: gli allergeni restano da verificare dal titolare.
  const canCreateIngredient = ["titolare", "super_admin", "responsabile", "addetto"].includes(ctx.user?.role);
  const enqueue = useSerialQueue();

  const [siteId, setSiteId] = useState(sites[0]?.id || "");
  const [drafts, setDrafts] = useState([]);
  const [session, setSession] = useState(null);
  const [pending, setPending] = useState([]); // foto in caricamento: { tempId, preview }
  const [ddtUploads, setDdtUploads] = useState([]); // pagine DDT in caricamento o fallite: { tempId, file, preview, error }
  const [previews, setPreviews] = useState({}); // file_id -> blob url locale (miniature immediate)
  const [view, setView] = useState("home"); // home | capture | item | summary
  const [itemId, setItemId] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [scanFor, setScanFor] = useState(null); // "new" | item id
  const [loading, setLoading] = useState(true);

  const photoInput = useRef(null);
  const galleryInput = useRef(null);
  const detailInput = useRef(null);
  const ddtInput = useRef(null);

  const loadDrafts = () => api.get(`${BASE}/sessions`).then(({ data }) => setDrafts(data.sessions || [])).catch(() => setDrafts([]));
  useEffect(() => {
    Promise.all([
      loadDrafts(),
      api.get("/app/catalog/ingredients").then(({ data }) => setIngredients((data.ingredients || []).filter((item) => !item.source_recipe_group_id))).catch(() => {}),
      api.get("/app/catalog/suppliers").then(({ data }) => setSuppliers(data.suppliers || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const ingredientName = (id) => ingredients.find((i) => i.id === id)?.name;
  const supplier = suppliers.find((s) => s.id === session?.supplier_id);
  const items = session?.items || [];
  const allCards = [...items, ...pending.map((p) => ({ id: p.tempId, pending: true, preview: p.preview, file_ids: [] }))];
  const thumb = (item) => item.preview || previews[item.file_ids?.[0]] || (item.file_ids?.[0] ? photoUrl(item.file_ids[0], "thumb") : null);

  const startSession = async (next) => {
    if (!siteId) { toast.error("Seleziona la sede"); return; }
    try {
      const { data } = await api.post(`${BASE}/sessions`, { site_id: siteId });
      setSession(data.session); setDdtUploads([]); setView(next);
    } catch (e) { toast.error(err(e)); }
  };
  const resume = (s, next = "capture") => { setSession(s); setDdtUploads([]); setView(next); };
  const deleteDraft = async (d, label) => {
    if (!window.confirm(`Vuoi eliminare l'acquisizione ${label}?\n\nI ${d.items.length} prodotti di questa bozza non verranno registrati. Le foto restano in archivio.`)) return;
    try { await api.delete(`${BASE}/sessions/${d.id}`); loadDrafts(); toast.success("Acquisizione eliminata"); }
    catch (e) { toast.error(err(e)); }
  };
  // Toglie una pagina del DDT dall'acquisizione ancora aperta (prima della conferma).
  const removeDdtPage = async (id, n) => {
    if (ddtBusy) { toast.error("Attendi che finisca il caricamento del DDT"); return; }
    if (!window.confirm(`Vuoi eliminare la pagina ${n + 1} del DDT?\n\nPotrai fotografarla di nuovo.`)) return;
    if (await patchSession({ ddt_file_ids: (session.ddt_file_ids || []).filter((x) => x !== id) })) toast.success("Pagina del DDT eliminata");
  };

  const patchSession = (body) => enqueue(async () => {
    try { const { data } = await api.patch(`${BASE}/sessions/${session.id}`, body); setSession(data.session); return true; }
    catch (e) { toast.error(err(e)); return false; }
  });

  const upload = async (file, kind) => {
    const blob = kind === "ddt" && file.type === "application/pdf" ? file : await compressImage(file);
    const fd = new FormData();
    fd.append("file", blob, kind === "ddt" && file.type === "application/pdf" ? file.name : "foto.jpg");
    const { data } = await api.post(`${BASE}/files?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { data, blob };
  };

  // FOTOGRAFA: ogni foto diventa subito un prodotto; il barcode viene cercato nella foto stessa.
  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !session) return;
    for (const file of files) {
      const tempId = `tmp-${Math.random().toString(36).slice(2)}`;
      const preview = URL.createObjectURL(file);
      setPending((p) => [...p, { tempId, preview }]);
      vibrate(40);
      (async () => {
        try {
          const { data: up, blob } = await upload(file, "etichetta");
          setPreviews((p) => ({ ...p, [up.id]: preview }));
          const barcode = (await decodeBarcodeFromBlob(file)) || (await decodeBarcodeFromBlob(blob));
          await enqueue(async () => {
            const { data } = await api.post(`${BASE}/sessions/${session.id}/items`, { file_ids: [up.id], barcode_raw: barcode || null });
            setSession(data.session);
            const it = data.session.items.find((i) => i.id === data.item_id);
            if (it && !it.missing.length) { vibrate([40, 60, 40]); toast.success(`${ingredientName(it.ingredient_id) || "Prodotto"}: riconosciuto con lotto e scadenza`); }
          });
        } catch (e) { toast.error(err(e)); }
        finally { setPending((p) => p.filter((x) => x.tempId !== tempId)); }
      })();
    }
  };

  const addFromBarcode = async (text) => {
    const target = scanFor;
    setScanFor(null);
    if (target && target !== "new") { await saveItem(target, { barcode_raw: text }); return; }
    await enqueue(async () => {
      try {
        const { data } = await api.post(`${BASE}/sessions/${session.id}/items`, { barcode_raw: text });
        setSession(data.session); toast.success("Codice letto");
      } catch (e) { toast.error(err(e)); }
    });
  };

  const scanHandler = useRef(null);
  scanHandler.current = addFromBarcode;
  const onScanText = useCallback((text) => scanHandler.current(text), []);

  const saveItem = (id, body) => enqueue(async () => {
    try { const { data } = await api.patch(`${BASE}/sessions/${session.id}/items/${id}`, body); setSession(data.session); return true; }
    catch (e) { toast.error(err(e)); return false; }
  });

  const addDetailPhoto = async (id, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    try {
      const { data: up, blob } = await upload(file, "dettaglio");
      setPreviews((p) => ({ ...p, [up.id]: URL.createObjectURL(file) }));
      const barcode = (await decodeBarcodeFromBlob(file)) || (await decodeBarcodeFromBlob(blob));
      if (await saveItem(id, { add_file_ids: [up.id], ...(barcode ? { barcode_raw: barcode } : {}) })) {
        vibrate(40); toast.success(barcode ? "Foto di dettaglio collegata, codice letto" : "Foto di dettaglio collegata al prodotto");
      }
    } catch (e) { toast.error(err(e)); }
  };

  // Ogni pagina si salva appena arriva e viene AGGIUNTA sul server (mai sostituita):
  // finché è in caricamento si vede la miniatura e non si può andare avanti né confermare.
  const uploadDdtPage = async (entry) => {
    setDdtUploads((list) => list.map((x) => (x.tempId === entry.tempId ? { ...x, error: null } : x)));
    try {
      const { data } = await upload(entry.file, "ddt");
      if (entry.preview) setPreviews((p) => ({ ...p, [data.id]: entry.preview }));
      if (!(await patchSession({ add_ddt_file_ids: [data.id] }))) throw new Error("Pagina non collegata all'acquisizione");
      setDdtUploads((list) => list.filter((x) => x.tempId !== entry.tempId));
      vibrate(40); toast.success("Pagina del DDT salvata");
    } catch (e) {
      const message = e.response ? err(e) : (e.message || "Connessione assente");
      setDdtUploads((list) => list.map((x) => (x.tempId === entry.tempId ? { ...x, error: message } : x)));
    }
  };
  const addDdtPages = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !session) return;
    const entries = files.map((file) => ({ tempId: `ddt-${Math.random().toString(36).slice(2)}`, file, preview: file.type === "application/pdf" ? null : URL.createObjectURL(file), error: null }));
    setDdtUploads((list) => [...list, ...entries]);
    entries.forEach(uploadDdtPage);
  };
  const discardDdtUpload = (entry) => {
    if (!window.confirm("Vuoi scartare questa pagina del DDT non caricata?\n\nPotrai fotografarla di nuovo.")) return;
    setDdtUploads((list) => list.filter((x) => x.tempId !== entry.tempId));
  };
  const ddtBusy = ddtUploads.some((x) => !x.error);
  const ddtFailed = ddtUploads.filter((x) => x.error);


  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const cancelSession = async () => {
    try { await api.delete(`${BASE}/sessions/${session.id}`); setConfirmCancel(false); setSession(null); setView("home"); loadDrafts(); toast.success("Acquisizione annullata"); }
    catch (e) { toast.error(err(e)); }
  };
  const deleteItem = (id) => enqueue(async () => {
    try { const { data } = await api.delete(`${BASE}/sessions/${session.id}/items/${id}`); setSession(data.session); setConfirmDeleteItem(null); setView("capture"); }
    catch (e) { toast.error(err(e)); }
  });

  const hiddenInputs = (
    <>
      <input ref={photoInput} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} data-testid="smart-photo-input" />
      <input ref={galleryInput} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
      <input ref={detailInput} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addDetailPhoto(itemId, e.target.files); e.target.value = ""; }} />
      <input ref={ddtInput} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { addDdtPages(e.target.files); e.target.value = ""; }} data-testid="smart-ddt-input" />
    </>
  );
  const overlays = (
    <>
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
      <LiveScanner open={Boolean(scanFor)} onClose={() => setScanFor(null)} onText={onScanText} />
      <ConfirmDeleteDialog open={confirmCancel} title="Annullare questa acquisizione?" confirmLabel="Sì, annulla acquisizione"
        description="I prodotti di questa bozza non verranno registrati. Le foto restano in archivio. Nessun lotto è stato ancora creato."
        onCancel={() => setConfirmCancel(false)} onConfirm={cancelSession} />
      <ConfirmDeleteDialog open={Boolean(confirmDeleteItem)} title="Togliere questo prodotto?" confirmLabel="Sì, togli prodotto"
        description="Il prodotto viene tolto da questa acquisizione e non verrà registrato."
        onCancel={() => setConfirmDeleteItem(null)} onConfirm={() => deleteItem(confirmDeleteItem)} />
    </>
  );

  // ---------------- HOME ----------------
  if (view === "home" || !session) {
    return (
      <div className="mx-auto max-w-xl space-y-5" data-testid="smart-lotto-page">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Arrivo merci</p>
          <h1 className="font-heading text-3xl font-bold text-navy">Carica lotti foto smart</h1>
          <p className="mt-1 text-sm text-slate-500">Fotografa il DDT o la fattura e dopo fotografa tutti i prodotti acquistati: il software legge i codici della confezione e ti chiede solo ciò che manca.</p>
        </div>
        {sites.length > 1 && <Field label="Sede di ricevimento"><Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>}
        <BigAction icon={FileText} title="Carica DDT" subtitle="Fotografa il DDT o la fattura e dopo fotografa tutti i prodotti acquistati" onClick={() => startSession("ddt")} testid="smart-start-ddt" />
        {loading ? <p className="text-sm text-slate-400">Caricamento…</p> : drafts.length > 0 && (
          <section className="space-y-3" data-testid="smart-drafts">
            <div>
              <h2 className="font-heading text-xl font-bold text-navy">Acquisizioni da completare <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 align-middle text-sm text-amber-800">{drafts.length}</span></h2>
              <p className="text-sm text-slate-500">Consegne iniziate ma non ancora confermate: i lotti non sono ancora nel magazzino.</p>
            </div>
            {drafts.map((d) => (
              <DraftCard key={d.id} draft={d} supplierName={suppliers.find((s) => s.id === d.supplier_id)?.name}
                siteName={sites.length > 1 ? sites.find((s) => s.id === d.site_id)?.name : null}
                ingredientName={ingredientName} previews={previews} onResume={resume} onDelete={deleteDraft} />
            ))}
          </section>
        )}
      </div>
    );
  }

  const header = (title, back) => (
    <div className="flex items-center gap-2">
      <button type="button" onClick={back} className="grid h-11 w-11 place-items-center rounded-full text-navy hover:bg-slate-100" aria-label="Indietro"><ChevronLeft className="h-6 w-6" /></button>
      <h1 className="font-heading text-2xl font-bold text-navy">{title}</h1>
    </div>
  );

  // ---------------- DDT ----------------
  if (view === "ddt") {
    return (
      <div className="mx-auto max-w-xl space-y-4 pb-28 lg:pb-0" data-testid="smart-ddt">
        {hiddenInputs}{overlays}
        {header("DDT", () => setView("capture"))}
        <BigAction icon={Camera} tone="red" title="Fotografa il DDT" subtitle="Una foto per pagina, oppure carica il PDF" onClick={() => ddtInput.current?.click()} testid="smart-ddt-photo" />
        {ddtFailed.length > 0 && <DdtUploadAlert failed={ddtFailed} />}
        {((session.ddt_file_ids || []).length > 0 || ddtUploads.length > 0) && (
          <div className="grid grid-cols-3 gap-2">{(session.ddt_file_ids || []).map((id, n) => (
            <div key={id} className="space-y-1">
              <button type="button" onClick={() => setLightbox(previews[id] || photoUrl(id))} className="relative block aspect-[3/4] w-full overflow-hidden rounded-lg border border-hairline bg-slate-50">
                {previews[id] ? <img src={previews[id]} alt={`Pagina ${n + 1}`} className="h-full w-full object-cover" /> : <span className="grid h-full place-items-center text-xs text-slate-500"><FileText className="h-6 w-6" />Pagina {n + 1}</span>}
              </button>
              <button type="button" onClick={() => removeDdtPage(id, n)} className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg border-2 border-red-200 bg-white px-2 text-sm font-semibold text-red-700 hover:border-red-400 hover:bg-red-50" aria-label={`Elimina pagina ${n + 1} del DDT`} data-testid={`smart-ddt-remove-${n}`}><Trash2 className="h-4 w-4" /> Elimina</button>
            </div>
          ))}
          {ddtUploads.map((u) => (
            <div key={u.tempId} className="space-y-1" data-testid={u.error ? "smart-ddt-failed" : "smart-ddt-uploading"}>
              <div className={`relative aspect-[3/4] w-full overflow-hidden rounded-lg border-2 ${u.error ? "border-red-500" : "border-navy/30"} bg-slate-50`}>
                {u.preview ? <img src={u.preview} alt="Pagina in caricamento" className={`h-full w-full object-cover ${u.error ? "" : "opacity-60"}`} /> : <span className="grid h-full place-items-center text-slate-500"><FileText className="h-6 w-6" /></span>}
                <span className={`absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 px-1 py-1 text-xs font-semibold text-white ${u.error ? "bg-red-600" : "bg-navy/80"}`}>
                  {u.error ? <><AlertTriangle className="h-3.5 w-3.5" /> Non caricata</> : <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Caricamento…</>}
                </span>
              </div>
              {u.error && (
                <div className="grid grid-cols-2 gap-1">
                  <button type="button" onClick={() => uploadDdtPage(u)} className="min-h-[44px] rounded-lg bg-red-600 text-sm font-semibold text-white hover:bg-red-700" data-testid="smart-ddt-retry">Riprova</button>
                  <button type="button" onClick={() => discardDdtUpload(u)} className="min-h-[44px] rounded-lg border-2 border-red-200 bg-white text-sm font-semibold text-red-700 hover:bg-red-50" aria-label="Scarta la pagina non caricata"><Trash2 className="mx-auto h-4 w-4" /></button>
                </div>
              )}
            </div>
          ))}</div>
        )}
        <SessionFields session={session} suppliers={suppliers} onPatch={patchSession} />
        <div className={ACTION_BAR}>
          <Button className={`w-full ${ddtBusy || ddtFailed.length ? "" : "smart-blink-green"}`} disabled={ddtBusy || ddtFailed.length > 0} onClick={() => setView("capture")} data-testid="smart-ddt-done">
            {ddtBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Attendi: caricamento del DDT…</> : ddtFailed.length ? "Riprova o scarta la pagina non caricata" : <>Ora fotografa i prodotti <ChevronRight className="h-4 w-4" /></>}
          </Button>
        </div>
      </div>
    );
  }

  // ---------------- SCHEDA PRODOTTO ----------------
  const current = items.find((i) => i.id === itemId);
  if (view === "item" && current) {
    const nextIncomplete = items.find((i) => i.id !== current.id && i.missing.length);
    return (
      <div className="mx-auto max-w-xl space-y-4 pb-28 lg:pb-0" data-testid="smart-item">
        {hiddenInputs}{overlays}
        {header("Controlla prodotto", () => setView("capture"))}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {current.file_ids.map((id) => (
            <button key={id} type="button" onClick={() => setLightbox(previews[id] || photoUrl(id))} className="relative h-40 w-32 shrink-0 overflow-hidden rounded-xl border border-hairline bg-slate-100">
              <img src={previews[id] || photoUrl(id, "thumb")} alt="Etichetta" className="h-full w-full object-cover" />
              <ZoomIn className="absolute bottom-1 right-1 h-6 w-6 rounded bg-black/50 p-1 text-white" />
            </button>
          ))}
          <button type="button" onClick={() => detailInput.current?.click()} className="grid h-40 w-32 shrink-0 place-items-center rounded-xl border-2 border-dashed border-navy/40 p-2 text-center text-xs font-semibold text-navy" data-testid="smart-detail-photo">
            <span><ImagePlus className="mx-auto mb-1 h-7 w-7" />SCATTA FOTO DETTAGLIO</span>
          </button>
        </div>
        <MissingGuide item={current} />
        <ItemEditor key={current.id} item={current} ingredients={ingredients} supplier={supplier} canCreateIngredient={canCreateIngredient}
          onIngredientCreated={(ing) => setIngredients((list) => [...list, ing])}
          onSave={(body) => saveItem(current.id, body)} onScan={() => setScanFor(current.id)} />
        <button type="button" onClick={() => setConfirmDeleteItem(current.id)} className="inline-flex min-h-[44px] items-center gap-2 text-sm text-red-600"><Trash2 className="h-4 w-4" /> Togli questo prodotto dall'acquisizione</button>
        <div className={`${ACTION_BAR} grid grid-cols-2 gap-2`}>
          <Button variant="outline" onClick={() => setView("capture")}>Torna all'elenco</Button>
          <Button variant="success" onClick={() => nextIncomplete ? setItemId(nextIncomplete.id) : setView("summary")} data-testid="smart-next">{nextIncomplete ? "Prossimo da completare" : "Riepilogo"} <ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    );
  }

  // ---------------- RIEPILOGO E CONFERMA ----------------
  if (view === "summary") {
    return (
      <div className="mx-auto max-w-xl space-y-4" data-testid="smart-summary">
        {hiddenInputs}{overlays}
        {header("Riepilogo", () => setView("capture"))}
        <Summary items={items} ingredientName={ingredientName} onOpen={(id) => { setItemId(id); setView("item"); }} />
        <SessionFields session={session} suppliers={suppliers} onPatch={patchSession} compact />
        {ddtFailed.length > 0 && <DdtUploadAlert failed={ddtFailed} onOpen={() => setView("ddt")} />}
        <ConfirmPanel session={session} items={items} supplier={supplier} ddtBusy={ddtBusy}
          onDone={(data) => {
            loadDrafts();
            if (data.remaining) { setSession(data.session); setView("capture"); }
            else { setSession(null); setView("home"); }
          }} />
      </div>
    );
  }

  // ---------------- ACQUISIZIONE (FOTOCAMERA) ----------------
  const complete = items.filter((i) => !i.missing.length).length;
  return (
    <div className="mx-auto max-w-xl space-y-4 pb-48 lg:pb-0" data-testid="smart-capture">
      {hiddenInputs}{overlays}
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Smart Lotto · {formatIt(session.received_date)}</p>
          <h1 className="font-heading text-2xl font-bold text-navy">Fotografa i prodotti</h1>
          <p className="text-sm text-slate-500">{supplier ? supplier.name : "Fornitore da indicare"}{session.ddt_number ? ` · DDT ${session.ddt_number}` : ""} · <button type="button" className="font-semibold text-navy underline" onClick={() => setView("ddt")}>{(session.ddt_file_ids || []).length ? "DDT caricato" : "aggiungi DDT"}</button></p>
        </div>
        <button type="button" onClick={() => setConfirmCancel(true)} className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-slate-400 hover:bg-slate-100" aria-label="Annulla acquisizione"><Trash2 className="h-5 w-5" /></button>
      </div>
      {ddtBusy && <p className="flex items-center gap-2 rounded-lg bg-navy/5 p-3 text-sm font-medium text-navy" data-testid="smart-ddt-busy"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento del DDT in corso: puoi già fotografare i prodotti.</p>}
      {ddtFailed.length > 0 && <DdtUploadAlert failed={ddtFailed} onOpen={() => setView("ddt")} />}
      <Card className="border-navy/20 bg-navy/5 p-4 text-sm text-navy">
        <b>Inquadra l'etichetta del prodotto</b>, possibilmente con il codice a barre visibile. Puoi scattare tutte le foto di fila e completare dopo.
      </Card>
      {allCards.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-400">Nessun prodotto ancora: premi “Fotografa prodotto”.</p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="smart-items">
          {allCards.map((item) => (
            <li key={item.id}>
              <button type="button" disabled={item.pending} onClick={() => { setItemId(item.id); setView("item"); }}
                className="w-full overflow-hidden rounded-xl border border-hairline bg-white text-left shadow-card">
                <div className="aspect-square bg-slate-100">{thumb(item) ? <img src={thumb(item)} alt="" className="h-full w-full object-cover" /> : <span className="grid h-full place-items-center text-slate-400"><ScanLine className="h-8 w-8" /></span>}</div>
                <div className="space-y-1 p-2">
                  <p className="truncate text-sm font-semibold text-navy">{ingredientName(item.ingredient_id) || (item.pending ? "Lettura…" : "Prodotto da scegliere")}</p>
                  <ItemBadge item={item} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className={`${ACTION_BAR} space-y-2`}>
        <Button className="smart-blink-red w-full !min-h-[56px] text-base" onClick={() => photoInput.current?.click()} data-testid="smart-take-photo"><Camera className="h-5 w-5" /> FOTOGRAFA PRODOTTO</Button>
        <div className="grid grid-cols-3 gap-2">
          <Button variant="outline" className="!px-2" onClick={() => setScanFor("new")}><ScanLine className="h-4 w-4" /> Barcode</Button>
          <Button variant="outline" className="!px-2" onClick={() => galleryInput.current?.click()}><ImagePlus className="h-4 w-4" /> Galleria</Button>
          <Button variant="success" className="!px-2" disabled={!items.length || pending.length > 0 || ddtBusy} onClick={() => setView("summary")} data-testid="smart-finish">Ho finito {items.length ? `(${complete}/${items.length})` : ""}</Button>
        </div>
      </div>
    </div>
  );
}

function SessionFields({ session, suppliers, onPatch, compact }) {
  const [ddt, setDdt] = useState(session.ddt_number || "");
  const [received, setReceived] = useState(formatIt(session.received_date));
  const receivedIso = parseQuickDate(received, "scadenza");
  return (
    <Card className="space-y-3 p-4">
      <Field label="Fornitore *">
        <Select value={session.supplier_id || ""} onChange={(e) => onPatch({ supplier_id: e.target.value || null })} data-testid="smart-supplier">
          <option value="">Seleziona il fornitore…</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Data ricevimento *" hint={receivedIso && received !== formatIt(receivedIso) ? `= ${formatIt(receivedIso)}` : null}>
          <Input inputMode="numeric" value={received} onChange={(e) => setReceived(e.target.value)}
            onBlur={() => { if (receivedIso) { setReceived(formatIt(receivedIso)); if (receivedIso !== session.received_date) onPatch({ received_date: receivedIso }); } }} />
        </Field>
        {!compact || session.ddt_number || session.ddt_file_ids?.length ? (
          <Field label="N. DDT">
            <Input value={ddt} autoCapitalize="characters" onChange={(e) => setDdt(e.target.value)} onBlur={() => { if (ddt.trim().toUpperCase() !== (session.ddt_number || "")) onPatch({ ddt_number: ddt }); }} data-testid="smart-ddt-number" />
          </Field>
        ) : null}
      </div>
    </Card>
  );
}

function ItemEditor({ item, ingredients, supplier, canCreateIngredient, onIngredientCreated, onSave, onScan }) {
  const [lot, setLot] = useState(item.supplier_lot || "");
  const [kind, setKind] = useState(item.expiry_kind || "scadenza");
  const [expiry, setExpiry] = useState(formatIt(item.expiry_date));
  const [qty, setQty] = useState(item.quantity ?? "");
  const [newName, setNewName] = useState(null);
  const expiryIso = parseQuickDate(expiry, kind);

  useEffect(() => { setLot(item.supplier_lot || ""); }, [item.supplier_lot]);
  useEffect(() => { setExpiry(formatIt(item.expiry_date)); }, [item.expiry_date]);

  const supplierIds = new Set(supplier?.ingredient_ids || []);
  const own = ingredients.filter((i) => supplierIds.has(i.id)).sort((a, b) => a.name.localeCompare(b.name));
  const others = ingredients.filter((i) => !supplierIds.has(i.id)).sort((a, b) => a.name.localeCompare(b.name));
  const notAssociated = item.ingredient_id && supplier?.ingredient_ids && !supplierIds.has(item.ingredient_id);
  // Ricerca per nome su tutte le materie prime: prima quelle del fornitore, poi le altre.
  const [query, setQuery] = useState("");
  const q = searchable(query);
  const matches = q ? [...own, ...others].filter((i) => searchable(i.name).includes(q)).slice(0, 8) : [];
  const pick = (id) => { setQuery(""); onSave({ ingredient_id: id }); };

  const saveExpiry = () => {
    if (!expiry.trim()) { if (item.expiry_date) onSave({ expiry_date: null }); return; }
    if (!expiryIso) { toast.error("Data non riconosciuta: scrivi per esempio 151026 oppure 15/10/2026"); return; }
    setExpiry(formatIt(expiryIso));
    if (expiryIso !== item.expiry_date || kind !== item.expiry_kind) onSave({ expiry_date: expiryIso, expiry_kind: kind });
  };
  // Calendario: alternativa alla scrittura a mano, salva subito la data scelta.
  const pickExpiry = (iso) => {
    if (!iso) return;
    setExpiry(formatIt(iso));
    if (iso !== item.expiry_date || kind !== item.expiry_kind) onSave({ expiry_date: iso, expiry_kind: kind });
  };
  const expiryEmpty = !expiry.trim();
  const createIngredient = async () => {
    try {
      const { data } = await api.post(`${BASE}/ingredients`, { name: newName.trim() });
      onIngredientCreated(data.ingredient); setNewName(null);
      await onSave({ ingredient_id: data.ingredient.id });
      toast.success(data.ingredient.allergens_to_verify
        ? "Nuovo prodotto aggiunto: il titolare riceverà un avviso per verificare gli allergeni"
        : "Nuovo prodotto aggiunto: verifica gli allergeni suggeriti in Materie prime");
    } catch (e) { toast.error(err(e)); }
  };

  return (
    <Card className="space-y-4 p-4">
      <div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gold" />
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca la materia prima per nome…"
            autoComplete="off" autoCorrect="off" spellCheck={false} aria-label="Cerca la materia prima per nome"
            className="h-12 w-full rounded-xl border-2 border-gold bg-blue-50 pl-10 pr-3 text-base text-navy placeholder:text-blue-700/60 focus:outline-none focus:ring-2 focus:ring-gold/40"
            data-testid="smart-ingredient-search" />
        </div>
        {q && (
          <div className="mt-2 space-y-1.5 rounded-xl bg-blue-50 p-2 ring-1 ring-blue-200" data-testid="smart-ingredient-results">
            {matches.map((i) => (
              <button type="button" key={i.id} onClick={() => pick(i.id)}
                className={`flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-left text-sm ring-1 hover:ring-gold ${i.id === item.ingredient_id ? "ring-gold font-semibold" : "ring-hairline"}`}
                data-testid={`smart-ingredient-pick-${i.id}`}>
                <span className="text-navy">{i.name}</span>
                {supplierIds.has(i.id) && <span className="shrink-0 text-xs text-slate-500">di {supplier.name}</span>}
              </button>
            ))}
            {!matches.length && <p className="px-2 py-1 text-sm text-slate-600">Nessuna materia prima trovata.</p>}
            {!matches.length && canCreateIngredient && (
              <button type="button" onClick={() => { setNewName(query.trim()); setQuery(""); }} className="min-h-[44px] w-full rounded-lg bg-white px-3 text-left text-sm font-semibold text-gold ring-1 ring-hairline hover:ring-gold">
                + Aggiungi «{query.trim()}» come nuovo prodotto
              </button>
            )}
          </div>
        )}
      </div>
      <Field label="Prodotto *" hint={item.ingredient_source === "barcode" ? "Riconosciuto dal barcode: conferma o cambia" : item.gtin ? `Barcode ${item.gtin}: alla conferma verrà ricordato per le prossime volte` : null}>
        <Select value={item.ingredient_id || ""} onChange={(e) => e.target.value === "__new__" ? setNewName("") : onSave({ ingredient_id: e.target.value || null })} data-testid="smart-ingredient">
          <option value="">Scegli il prodotto…</option>
          {own.length > 0 && <optgroup label={`Prodotti di ${supplier.name}`}>{own.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</optgroup>}
          <optgroup label={own.length ? "Altri prodotti" : "Prodotti"}>{others.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</optgroup>
          {canCreateIngredient && <option value="__new__">+ Nuovo prodotto…</option>}
        </Select>
      </Field>
      {notAssociated && <p className="-mt-2 text-xs text-slate-500">Alla conferma verrà associato al fornitore {supplier.name}.</p>}
      {newName !== null && (
        <div className="flex gap-2">
          <Input autoFocus placeholder="Nome del nuovo prodotto" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <Button className="shrink-0 !whitespace-nowrap" onClick={createIngredient} disabled={!newName.trim()}>Aggiungi</Button>
        </div>
      )}
      <Field label="Lotto *" hint={item.lot_source === "gs1" ? `✓ ${SOURCE_LABEL.gs1}` : "Su iPhone: tocca il campo e usa “Scansiona testo” per leggerlo con la fotocamera"}>
        <Input value={lot} autoCapitalize="characters" autoComplete="off" autoCorrect="off" spellCheck={false}
          onChange={(e) => setLot(e.target.value.toUpperCase())} onBlur={() => { if (lot.trim() !== (item.supplier_lot || "")) onSave({ supplier_lot: lot }); }} data-testid="smart-lot" />
      </Field>
      <div>
        <div className="mb-1.5 flex items-center gap-2">
          {[["scadenza", "Scadenza"], ["tmc", "TMC (preferibilmente entro)"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => { setKind(k); if (item.expiry_date) onSave({ expiry_kind: k }); }}
              className={`min-h-[36px] rounded-full px-3 text-xs font-semibold ${kind === k ? (expiryEmpty ? "smart-blink-red text-white" : "bg-navy text-white") : "bg-slate-100 text-slate-600"}`}>{label}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input inputMode="numeric" placeholder={kind === "tmc" ? "es. 1026 = fine ottobre 2026" : "es. 151026 = 15/10/2026"} value={expiry}
            onChange={(e) => setExpiry(e.target.value)} onBlur={saveExpiry} className={`flex-1 ${expiryEmpty ? "smart-blink-field" : ""}`} data-testid="smart-expiry" />
          {/* Il campo data trasparente sta sopra l'icona: un tocco apre il calendario nativo (anche su iPhone). */}
          <label className="relative grid h-12 w-12 shrink-0 cursor-pointer place-items-center rounded-xl border-2 border-red-300 bg-red-50 text-red-700" title="Scegli dal calendario">
            <CalendarDays className="h-6 w-6" aria-hidden="true" />
            <input type="date" value={expiryIso || ""} onChange={(e) => pickExpiry(e.target.value)} aria-label="Scegli la data dal calendario"
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0" data-testid="smart-expiry-calendar" />
          </label>
        </div>
        <span className="mt-1 block text-xs text-slate-400">{item.expiry_source === "gs1" ? `✓ ${SOURCE_LABEL.gs1}` : expiryIso && expiry !== formatIt(expiryIso) ? `= ${formatIt(expiryIso)}` : "Scrivi i numeri oppure tocca il calendario"}</span>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <Field label="Quantità (facoltativa)">
          <Input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)}
            onBlur={() => { const n = Number(String(qty).replace(",", ".")); if (qty === "" && item.quantity != null) onSave({ clear_quantity: true }); else if (qty !== "" && n > 0 && n !== item.quantity) onSave({ quantity: n }); }} />
        </Field>
        <Field label="Unità"><Select value={item.unit || "conf"} onChange={(e) => onSave({ unit: e.target.value })}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</Select></Field>
      </div>
      {item.warnings?.length > 0 && (
        <ul className="space-y-1">{item.warnings.map((w) => (
          <li key={w.code} className={`flex gap-2 rounded-lg p-2 text-sm ${w.level === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{w.message}</li>
        ))}</ul>
      )}
      <Button variant="ghost" className="w-full" onClick={onScan}><ScanLine className="h-4 w-4" /> Leggi barcode / DataMatrix dal vivo</Button>
    </Card>
  );
}

// Avviso fisso (non un toast che sparisce): una pagina del DDT non è arrivata al server.
function DdtUploadAlert({ failed, onOpen }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border-2 border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert" data-testid="smart-ddt-alert">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{failed.length === 1 ? "Una pagina del DDT non è stata caricata" : `${failed.length} pagine del DDT non sono state caricate`}</p>
        <p className="text-xs">{failed[0].error}. Controlla la connessione e premi «Riprova».</p>
        {onOpen && <button type="button" onClick={onOpen} className="mt-2 min-h-[44px] rounded-lg bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700">Vai al DDT</button>}
      </div>
    </div>
  );
}

// "oggi alle 10:32", "ieri alle 18:05", "14/10 alle 09:12"
function whenIt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (days === 0) return `oggi alle ${time}`;
  if (days === 1) return `ieri alle ${time}`;
  return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })} alle ${time}`;
}

// Scheda di una bozza: chi, cosa, quanto è già stato fatto e cosa manca, con un unico
// pulsante che riporta al passo giusto (DDT, prodotti da completare o conferma).
function DraftCard({ draft, supplierName, siteName, ingredientName, previews, onResume, onDelete }) {
  const items = draft.items || [];
  const incomplete = items.filter((i) => i.missing.length);
  const done = items.length - incomplete.length;
  const pages = (draft.ddt_file_ids || []).length;
  const confirmed = (draft.confirmed_items || []).length;
  const ageDays = draft.received_date ? Math.floor((Date.now() - new Date(`${draft.received_date}T00:00:00`).getTime()) / 86400000) : 0;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;
  const thumbs = items.filter((i) => i.file_ids?.[0]).slice(0, 4);
  const label = `del ${formatIt(draft.received_date)}${supplierName ? ` (${supplierName})` : ""}`;
  const next = !items.length ? (pages ? ["capture", "Fotografa i prodotti"] : ["ddt", "Fotografa il DDT"])
    : incomplete.length ? ["capture", `Completa ${incomplete.length} ${incomplete.length === 1 ? "prodotto" : "prodotti"}`]
    : ["summary", "Vai alla conferma"];
  const status = !items.length ? ["Nessun prodotto", "bg-slate-100 text-slate-600"]
    : incomplete.length ? [`${incomplete.length} da completare`, "bg-amber-100 text-amber-800"]
    : ["Pronta da confermare", "bg-emerald-100 text-emerald-800"];
  const shown = [...incomplete, ...items.filter((i) => !i.missing.length)].slice(0, 4);

  return (
    <Card className="overflow-hidden p-0" data-testid={`smart-draft-${draft.id}`}>
      <div className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={`truncate font-heading text-lg font-bold ${supplierName ? "text-navy" : "text-red-600"}`}>{supplierName || "Fornitore da indicare"}</p>
            <p className="text-sm text-slate-500">Merce ricevuta il <b className="text-navy">{formatIt(draft.received_date)}</b>{siteName ? ` · ${siteName}` : ""}</p>
          </div>
          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${status[1]}`}>{status[0]}</span>
        </div>

        <div className="flex flex-wrap gap-2 text-xs font-medium">
          {pages
            ? <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-emerald-800"><FileText className="h-3.5 w-3.5" />DDT{draft.ddt_number ? ` n. ${draft.ddt_number}` : ""} · {pages} {pages === 1 ? "pagina" : "pagine"}</span>
            : <span className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-2 py-1 text-red-700"><FileText className="h-3.5 w-3.5" />DDT non fotografato</span>}
          <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-slate-700"><Camera className="h-3.5 w-3.5" />{items.length} {items.length === 1 ? "prodotto" : "prodotti"}</span>
          {confirmed > 0 && <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-emerald-800"><PackageCheck className="h-3.5 w-3.5" />{confirmed} già registrati</span>}
        </div>

        {items.length > 0 && (
          <div>
            <div className="mb-1 flex justify-between text-xs text-slate-500"><span>Prodotti completi</span><span className="font-semibold text-navy">{done} di {items.length}</span></div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${incomplete.length ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} /></div>
          </div>
        )}

        {thumbs.length > 0 && (
          <div className="flex gap-2">
            {thumbs.map((i) => <img key={i.id} src={previews[i.file_ids[0]] || photoUrl(i.file_ids[0], "thumb")} alt="" className="h-14 w-14 rounded-lg border border-hairline object-cover" />)}
            {items.length > thumbs.length && <span className="grid h-14 w-14 place-items-center rounded-lg bg-slate-100 text-sm font-semibold text-slate-600">+{items.length - thumbs.length}</span>}
          </div>
        )}

        {shown.length > 0 && (
          <ul className="divide-y divide-hairline rounded-lg border border-hairline text-sm">
            {shown.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-2 px-3 py-2">
                <span className="min-w-0">
                  <span className={`block truncate font-semibold ${i.ingredient_id ? "text-navy" : "text-red-600"}`}>{ingredientName(i.ingredient_id) || "Prodotto da riconoscere"}</span>
                  <span className="block truncate text-xs text-slate-500">Lotto {i.supplier_lot || "—"} · {i.expiry_kind === "tmc" ? "TMC" : "Scad."} {formatIt(i.expiry_date) || "—"}</span>
                </span>
                {i.missing.length
                  ? <span className="shrink-0 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">manca {i.missing.map((m) => MISSING_LABEL[m]).join(", ")}</span>
                  : <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" aria-label="Completo" />}
              </li>
            ))}
            {items.length > shown.length && <li className="px-3 py-2 text-xs text-slate-500">e altri {items.length - shown.length} prodotti</li>}
          </ul>
        )}

        {ageDays >= 2 && <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />Merce ricevuta {ageDays} giorni fa: confermala per avere i lotti disponibili in produzione.</p>}
        <p className="text-xs text-slate-400">Iniziata {whenIt(draft.created_at)}{draft.created_by_name ? ` da ${draft.created_by_name}` : ""}{draft.updated_at && draft.updated_at !== draft.created_at ? ` · ultima modifica ${whenIt(draft.updated_at)}` : ""}</p>
      </div>
      <div className="flex gap-2 border-t border-hairline bg-slate-50 p-3">
        <Button variant="success" className="flex-1 !min-h-[48px]" onClick={() => onResume(draft, next[0])} data-testid={`smart-draft-resume-${draft.id}`}>{next[1]} <ChevronRight className="h-4 w-4" /></Button>
        <button type="button" onClick={() => onDelete(draft, label)} className="grid min-h-[48px] w-12 place-items-center rounded-lg border-2 border-red-200 bg-white text-red-700 hover:border-red-400 hover:bg-red-50" aria-label={`Elimina l'acquisizione ${label}`} data-testid={`smart-draft-delete-${draft.id}`}><Trash2 className="h-5 w-5" /></button>
      </div>
    </Card>
  );
}

function Summary({ items, ingredientName, onOpen }) {
  const n = items.length;
  const withProduct = items.filter((i) => i.ingredient_id).length;
  const withLot = items.filter((i) => (i.supplier_lot || "").trim()).length;
  const withExpiry = items.filter((i) => i.expiry_date).length;
  const attention = items.filter((i) => i.missing.length || i.warnings?.length);
  const row = (ok, text) => <li className={`flex items-center gap-2 ${ok ? "text-emerald-700" : "text-amber-700"}`}>{ok ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}{text}</li>;
  return (
    <Card className="space-y-4 p-5">
      <div>
        <p className="font-heading text-3xl font-bold text-navy">{n} {n === 1 ? "prodotto acquisito" : "prodotti acquisiti"}</p>
        <ul className="mt-3 space-y-1.5 text-sm font-medium">
          {row(withProduct === n, `${withProduct} prodotti riconosciuti`)}
          {row(withLot === n, `${withLot} lotti acquisiti`)}
          {row(withExpiry === n, `${withExpiry} scadenze acquisite`)}
          {withExpiry < n && row(false, `${n - withExpiry} scadenze mancanti`)}
        </ul>
      </div>
      {attention.length > 0 ? (
        <div>
          <p className="mb-2 font-semibold text-navy">Richiedono la tua attenzione: {attention.length}</p>
          <ul className="space-y-2">{attention.map((i) => (
            <li key={i.id}><button type="button" onClick={() => onOpen(i.id)} className="flex min-h-[52px] w-full items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left text-sm">
              <span><b className="text-navy">{ingredientName(i.ingredient_id) || "Prodotto da scegliere"}</b><br /><span className="text-amber-800">{i.missing.length ? `Manca ${i.missing.map((m) => MISSING_LABEL[m]).join(", ")}` : i.warnings[0]?.message}</span></span>
              <ChevronRight className="h-5 w-5 shrink-0 text-amber-700" />
            </button></li>
          ))}</ul>
        </div>
      ) : <p className="flex items-center gap-2 font-semibold text-emerald-700"><PackageCheck className="h-5 w-5" /> Tutto completo: puoi confermare.</p>}
    </Card>
  );
}

function ConfirmPanel({ session, items, supplier, ddtBusy, onDone }) {
  const [problem, setProblem] = useState(false);
  const [checks, setChecks] = useState(ALL_OK);
  const [outcome, setOutcome] = useState("accettata");
  const [note, setNote] = useState("");
  const [temperature, setTemperature] = useState("");
  const hasDdt = (session.ddt_file_ids || []).length > 0;
  const [docPending, setDocPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const temperatureText = temperature.trim().replace(",", ".");
  const temperatureValue = temperatureText ? Number(temperatureText) : null;
  const temperatureInvalid = temperatureText !== "" && !Number.isFinite(temperatureValue);
  const rejected = problem && outcome === "respinta";
  const ready = items.filter((i) => (rejected ? !i.missing.includes("prodotto") : !i.missing.length));
  const leftOut = items.length - ready.length;
  const issues = CHECK_QUESTIONS.filter((q) => checks[q.key] === "no").length;
  const blocked = ddtBusy || !supplier || !ready.length || temperatureInvalid || (!hasDdt && !docPending) || (problem && (CHECK_QUESTIONS.some((q) => !checks[q.key]) || ((issues || rejected) && !note.trim())));

  const confirm = async (allOk) => {
    setBusy(true);
    try {
      const { data } = await api.post(`${BASE}/sessions/${session.id}/confirm`, {
        checks: { ...(allOk ? ALL_OK : checks), temperature_c: temperatureValue }, outcome: allOk ? "accettata" : outcome, note: note.trim(), associate_suppliers: true, document_pending: !hasDdt && docPending,
      });
      vibrate([60, 80, 60]);
      if (data.receipt?.outcome === "respinta") toast.warning("Merce respinta registrata: è stata aperta una non conformità.");
      else if (data.receipt?.status === "non_conforme") toast.warning(`${data.lots_created} lotti registrati con anomalie: aperta una non conformità.`);
      else toast.success(`Acquisizione completata: ${data.lots_created} ${data.lots_created === 1 ? "lotto registrato" : "lotti registrati"}`);
      if (data.remaining) toast.info(`${data.remaining} prodotti incompleti restano da completare in questa acquisizione`);
      onDone(data);
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  const temperatureField = (
    <Field label="Temperatura rilevata in °C (facoltativa)">
      <input type="text" inputMode="decimal" value={temperature} onChange={(e) => setTemperature(e.target.value)} placeholder="Es. 3,5"
        className={`min-h-[44px] w-full rounded-lg border p-3 text-sm ${temperatureInvalid ? "border-red-500 ring-1 ring-red-300" : "border-hairline"}`} data-testid="smart-receipt-temperature" />
    </Field>
  );

  return (
    <Card className="space-y-3 p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Controllo di accettazione della merce</p>
      {!supplier && <p className="text-sm text-amber-700">Indica il fornitore per poter confermare.</p>}
      {!hasDdt && (
        <label className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 text-sm ${docPending ? "border-amber-400 bg-amber-50 text-amber-900" : "border-red-200 bg-white text-slate-700"}`} data-testid="smart-doc-pending">
          <input type="checkbox" checked={docPending} onChange={(e) => setDocPending(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-amber-600" data-testid="smart-doc-pending-check" />
          <span><span className="font-semibold">Manca il DDT o la fattura.</span> Fotografalo con «Carica DDT», oppure spunta qui: non ho il documento adesso, lo allegherò in seguito (dal registro ricevimenti in Lotti registrati).</span>
        </label>
      )}
      {leftOut > 0 && ready.length > 0 && <p className="text-sm text-slate-600">Verranno registrati {ready.length} prodotti completi; {leftOut} restano in bozza da completare.</p>}
      {!problem ? (
        <>
          <Button variant="success" className="w-full !min-h-[56px] text-base" loading={busy} disabled={blocked} onClick={() => confirm(true)} data-testid="smart-confirm-all-ok">
            <CheckCircle2 className="h-5 w-5" /> Tutto conforme · CONFERMA ACQUISIZIONE
          </Button>
          <Button variant="outline" className="w-full" onClick={() => setProblem(true)}>C'è un problema con la consegna</Button>
          <details className="group rounded-lg border border-hairline" data-testid="smart-receipt-details">
            <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-2 px-3 text-sm font-semibold text-navy">Aggiungi dettagli (facoltativo)<span className="text-xs font-normal text-slate-500 group-open:hidden">temperatura, note</span></summary>
            <div className="space-y-3 border-t border-hairline p-3">
              {temperatureField}
              <Field label="Note"><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full rounded-lg border border-hairline p-3 text-sm" data-testid="smart-receipt-note" /></Field>
            </div>
          </details>
        </>
      ) : (
        <div className="space-y-3">
          {CHECK_QUESTIONS.map((q) => (
            <div key={q.key}>
              <p className="mb-1 text-sm font-medium text-navy">{q.label}{q.hint && <span className="block text-xs font-normal text-slate-500">{q.hint}</span>}</p>
              <div className="flex gap-2">{[["si", "Sì"], ["no", "No"], ...(q.na ? [["na", "Non applicabile"]] : [])].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setChecks({ ...checks, [q.key]: v })}
                  className={`min-h-[44px] flex-1 rounded-lg border text-sm font-semibold ${checks[q.key] === v ? (v === "no" ? "border-red-500 bg-red-50 text-red-700" : "border-navy bg-navy text-white") : "border-hairline text-slate-600"}`}>{label}</button>
              ))}</div>
            </div>
          ))}
          <div className="flex gap-2">{[["accettata", "Merce accettata"], ["respinta", "Merce respinta"]].map(([v, label]) => (
            <button key={v} type="button" onClick={() => setOutcome(v)} className={`min-h-[44px] flex-1 rounded-lg border text-sm font-semibold ${outcome === v ? (v === "respinta" ? "border-red-500 bg-red-50 text-red-700" : "border-navy bg-navy text-white") : "border-hairline text-slate-600"}`}>{label}</button>
          ))}</div>
          {temperatureField}
          <Field label={issues || rejected ? "Cosa hai riscontrato e cosa hai fatto *" : "Note"}>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full rounded-lg border border-hairline p-3 text-sm" />
          </Field>
          <Button variant="success" className="w-full !min-h-[56px]" loading={busy} disabled={blocked} onClick={() => confirm(false)} data-testid="smart-confirm-problem">CONFERMA ACQUISIZIONE</Button>
          <Button variant="ghost" className="w-full" onClick={() => setProblem(false)}>Torna a “Tutto conforme”</Button>
        </div>
      )}
    </Card>
  );
}
